import { Component } from "react"
import Icon from "components/planning_center_icon"
import { Menu, MenuButton, MenuList, MenuLink } from "@reach/menu-button"
import { StackView } from "@planningcenter/tapestry-react"

class CheckInsDropdown extends Component {
  constructor(props) {
    super(props)
    this.state = {
      existingList: [],
      newList: [],
      loading: true,
      ableToEdit: false,
      disabled: false,
      authenticityToken: document.querySelector("head meta[name='csrf-token']").content
    }
  }

  componentDidMount() {
    const {
      apiBaseUrl,
      event: { id: eventId }
    } = this.props

    const hasEditorPermissions = fetch(`${apiBaseUrl}/me`, {
      credentials: "include"
    })
      .then(response => {
        return response.json()
      })
      .then(data => {
        return data.data.attributes.permission
      })

    const checkins = fetch(
      `${apiBaseUrl}/events?where[registration_event_id]=${eventId}&order=name`,
      {
        credentials: "include"
      }
    )
      .then(response => {
        return response.json()
      })
      .then(data => {
        return data.data
      })

    Promise.all([hasEditorPermissions, checkins])
      .then(responses => {
        const [permission, checkInEvents] = responses
        const dividedCheckins = this.divideCheckIns(checkInEvents)

        this.setState({
          ableToEdit: permission === "Editor" ? true : false,
          ...dividedCheckins,
          loading: false,
          disabled: permission === "Editor" ? false : true
        })
      })
      .catch(() => {
        this.setState({ disabled: true, loading: false })
      })
  }

  divideCheckIns(checkInEvents) {
    const existingResults = checkInEvents.map(event => this.decorateCheckInEvent(event))

    const newResults = []
    const rootEventIsPresent = existingResults.find(event => event.rootEvent === true)
    if (!rootEventIsPresent) {
      newResults.push(this.decorateRegistrationEvent(this.props.event))
    }
    this.props.assignmentTypes.forEach(assignment => {
      const matchingAssignmentInExistingList = existingResults.find(
        entry => entry.assignmentId === assignment.id.toString()
      )
      if (!matchingAssignmentInExistingList) {
        newResults.push(this.decorateRegistrationEventAssignment(assignment))
      }
    })

    return {
      existingList: existingResults,
      newList: newResults
    }
  }

  decorateCheckInEvent(event) {
    const result = {}
    const assignmentRegex = event.attributes.integration_key.match(/assignment-type:(\d+)\|/)
    if (assignmentRegex && assignmentRegex.length > 1) {
      result.assignmentId = assignmentRegex[1]
      result.url = `${this.props.checkInsBaseUrl}/registrations/?event_id=${this.props.event.id}&assignment_type_id=${result.assignmentId}`
    } else {
      result.rootEvent = true
      result.url = `${this.props.checkInsBaseUrl}/registrations/?event_id=${this.props.event.id}`
    }
    result.name = event.attributes.name
    return result
  }

  decorateRegistrationEvent(event) {
    return {
      url: `${this.props.checkInsBaseUrl}/registrations/?event_id=${event.id}`,
      name: event.name
    }
  }

  decorateRegistrationEventAssignment(event) {
    return {
      url: `${this.props.checkInsBaseUrl}/registrations/?event_id=${this.props.event.id}&assignment_type_id=${event.id}`,
      name: `${this.props.event.name}: ${event.name}`
    }
  }

  renderLoadingState() {
    return (
      <div className="btn-solid interaction">
        <StackView axis="horizontal" alignment="center">
          <Icon symbol="check-ins#check" />
          Loading...
        </StackView>
      </div>
    )
  }

  renderDropdown() {
    return (
      <Menu>
        <MenuButton
          className="btn-solid interaction"
          css={{
            "[aria-expanded='true']": {
              background: "var(--t-fill-color-product-checkins-050)",
              color: "var(--t-fill-color-neutral-100)"
            }
          }}
        >
          <Icon symbol="check-ins#check" />
          Check In
          <span data-reach-menu-button-toggle-icon>
            <Icon symbol="general#down-caret" />
          </span>
        </MenuButton>

        <MenuList>
          <div className="font-size-4 px-2 my-2">
            Integrate with{" "}
            <span style={{ color: "var(--t-fill-color-product-checkins-050)" }}>Check-Ins</span>{" "}
            for a fast and secure way to track Registrants.{" "}
            <a
              href="https://pcocheck-ins.zendesk.com/hc/en-us/articles/25103685567515"
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            </a>
          </div>
          {this.state.disabled ? <DisabledText /> : <EventLists {...this.state} />}
        </MenuList>
      </Menu>
    )
  }

  render() {
    return this.state.loading ? this.renderLoadingState() : this.renderDropdown()
  }
}

const DisabledText = () => {
  return (
    <div className="line p-2 fd-c jc-s as-fs font-size-4">
      Uh oh! You don't have access to manage events in Check-ins. To use Check-ins with this event,
      contact a Check-ins Administrator.
    </div>
  )
}

const EventLists = ({ existingList, newList, authenticityToken, ableToEdit }) => {
  return (
    <>
      <EventList
        list={existingList}
        authenticityToken={authenticityToken}
        text="Existing Events"
        ableToEdit={ableToEdit}
      />

      <EventList
        list={newList}
        authenticityToken={authenticityToken}
        text="Create New Event"
        ableToEdit={ableToEdit}
      />
    </>
  )
}

const EventList = ({ list, authenticityToken, text, ableToEdit }) => {
  if (list.length === 0) {
    return null
  } else {
    return (
      <div>
        <h4 className="line mb-0 mt-1 pt-2 font-size-5 pb-3p px-2 tt-u fw-600 c-neutral-700">
          {text}
        </h4>
        {list.map((checkIn, index) => (
          <FormButton
            checkIn={checkIn}
            index={index}
            key={index}
            authenticityToken={authenticityToken}
            ableToEdit={ableToEdit}
          />
        ))}
      </div>
    )
  }
}

const FormButton = ({ checkIn, index, authenticityToken, ableToEdit }) => {
  return (
    <form key={index} action={checkIn.url} method="post" target="_blank" css={{ padding: 0 }}>
      <input type="hidden" name="authenticity_token" value={authenticityToken} />
      <input
        type="submit"
        name="commit"
        value={checkIn.name}
        className="dropdown-menu-item"
        css={{
          backgroundColor: "inherit",
          textAlign: "left",
          whiteSpace: "normal",
          width: "100%"
        }}
      />
    </form>
  )
}

export default CheckInsDropdown
